import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Nav, { WHATSAPP_ORDER_URL } from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";

const EASE = [0.22, 1, 0.36, 1];

const QUICK_LINKS = [
  { to: "/", label: "Home" },
  { to: "/book", label: "Book a pickup" },
  { to: "/plans", label: "Subscriptions" },
  { to: "/services", label: "Services" },
  { to: "/blog", label: "Journal" },
  { to: "/track", label: "Track order" },
];

export default function NotFound() {
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="not-found-page">
      <Seo
        title="Page not found (404)"
        description="The page you were looking for doesn't exist. Head back home or book a laundry pickup with RIZ N WASH — Gurugram's premium doorstep laundry & dry cleaning brand."
        path={pathname || "/404"}
        noindex
      />
      <Nav />

      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-16 md:pt-28 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <div className="overline mb-4 text-rw-blue" data-testid="not-found-eyebrow">
            404 · Not found
          </div>
          <h1
            className="font-display font-light tracking-tight text-rw-ink leading-[1.02] text-4xl sm:text-5xl lg:text-6xl"
            data-testid="not-found-heading"
          >
            This page went out for a wash.
            <br />
            <em className="italic font-normal text-rw-blue">And never came back.</em>
          </h1>
          <p
            className="mt-6 md:mt-8 text-lg md:text-xl text-rw-ink2 max-w-2xl leading-relaxed"
            data-testid="not-found-subtext"
          >
            We couldn't find <code className="px-2 py-0.5 rounded bg-rw-bg2 text-sm font-mono">{pathname}</code>. It may
            have been moved, renamed, or you might have followed a broken link. Either way — here's how to get back on
            track.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
          className="mt-10 md:mt-14 flex flex-wrap items-center gap-3"
        >
          <Link
            to="/"
            className="btn-dark rounded-full px-6 py-3 text-sm md:text-base font-semibold"
            data-testid="not-found-home-btn"
          >
            Go home →
          </Link>
          <a
            href={WHATSAPP_ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full px-6 py-3 text-sm md:text-base font-semibold border border-rw-ink/15 text-rw-ink hover:bg-rw-ink hover:text-white transition-colors"
            data-testid="not-found-book-btn"
          >
            Order on WhatsApp
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.25 }}
          className="mt-16 md:mt-20 border-t border-rw-ink/10 pt-10"
        >
          <div className="overline mb-4">Or try one of these</div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="not-found-quick-links">
            {QUICK_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="group rounded-2xl border border-rw-ink/10 bg-white p-5 hover:border-rw-blue transition-colors"
                data-testid={`not-found-link-${l.to.replace(/\//g, "") || "home"}`}
              >
                <div className="font-display text-lg text-rw-ink group-hover:text-rw-blue transition-colors">
                  {l.label}
                </div>
                <div className="text-xs text-rw-ink3 mt-1 font-mono">{l.to}</div>
              </Link>
            ))}
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
